import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Avatar,
  Divider,
  LinearProgress,
  InputAdornment,
  Collapse,
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Visibility,
  Image,
  PictureAsPdf,
  InsertDriveFile,
  FilterList,
  Search,
  Refresh,
  Schedule,
  VerifiedUser,
  Close,
  ExpandMore,
  ExpandLess,
  Assignment,
  PendingActions,
  WarningAmber,
  Flag,
  StickyNote2,
  Timeline,
  LocalOffer,
  Description,
  Shield,
} from '@mui/icons-material';
import { documentsAPI } from 'services/api';

// ═══════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════

const DOC_TYPE_LABELS = {
  proposal_form: 'Proposal Form',
  CAA_registration: 'CAA Registration',
  pond_ownership: 'Pond Ownership',
  seed_invoice: 'Seed Invoice',
  input_cost_receipts: 'Input Receipts',
  logbook: 'Logbook',
  bank_passbook: 'Bank Passbook',
  SPF_certificate: 'SPF Certificate',
  soil_test: 'Soil Test',
  water_test: 'Water Test',
  previous_sale_deed: 'Sale Deed',
  geospatial_image: 'Geospatial Image',
  aadhar_front: 'Aadhaar Front',
  aadhar_back: 'Aadhaar Back',
  pan_photo: 'PAN Card',
  policy_application: 'Policy App',
  farm_registration: 'Farm Reg.',
  identity_proof: 'ID Proof',
  address_proof: 'Address Proof',
  bank_statement: 'Bank Statement',
  farm_photos: 'Farm Photos',
  pond_construction: 'Pond Cert.',
  water_quality: 'Water Quality',
  claim_evidence: 'Claim Evidence',
  medical_report: 'Medical Report',
  loss_assessment: 'Loss Assessment',
  other: 'Other',
};

/** Derive a tag category from doc_type / document_type for tagging system */
const TAG_CATEGORIES = {
  KYC: {
    label: 'KYC', color: '#7c3aed', bg: '#ede9fe',
    types: ['aadhar_front', 'aadhar_back', 'pan_photo', 'identity_proof', 'address_proof'],
  },
  Invoice: {
    label: 'Invoice', color: '#d97706', bg: '#fef3c7',
    types: ['seed_invoice', 'input_cost_receipts', 'bank_passbook', 'bank_statement', 'previous_sale_deed'],
  },
  Policy: {
    label: 'Policy', color: '#2563eb', bg: '#dbeafe',
    types: ['proposal_form', 'CAA_registration', 'pond_ownership', 'SPF_certificate', 'policy_application', 'farm_registration', 'pond_construction'],
  },
  Claim: {
    label: 'Claim', color: '#dc2626', bg: '#fecaca',
    types: ['claim_evidence', 'medical_report', 'loss_assessment'],
  },
  Other: {
    label: 'Other', color: '#64748b', bg: '#f1f5f9',
    types: ['logbook', 'soil_test', 'water_test', 'geospatial_image', 'farm_photos', 'water_quality', 'other'],
  },
};

const getDocTag = (docType) => {
  const dtype = (docType || '').toLowerCase();
  for (const [, cat] of Object.entries(TAG_CATEGORIES)) {
    if (cat.types.some(t => dtype.includes(t) || t.includes(dtype))) return cat;
  }
  return TAG_CATEGORIES.Other;
};

const HIGH_PRIORITY_TYPES = ['identity_proof', 'claim_evidence', 'aadhar_front', 'aadhar_back', 'pan_photo', 'policy_application', 'loss_assessment'];
const isHighRisk = (doc) => {
  const dtype = (doc.doc_type || doc.document_type || '').toLowerCase();
  return HIGH_PRIORITY_TYPES.some(t => dtype.includes(t));
};

const getDocIcon = (fileName) => {
  const ext = (fileName || '').split('.').pop()?.toLowerCase();
  if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext)) return <Image sx={{ fontSize: 16 }} />;
  if (ext === 'pdf') return <PictureAsPdf sx={{ fontSize: 16 }} />;
  return <InsertDriveFile sx={{ fontSize: 16 }} />;
};

const isImageFile = (fileName) => {
  const ext = (fileName || '').split('.').pop()?.toLowerCase();
  return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext);
};

const isPdfFile = (fileName) => {
  const ext = (fileName || '').split('.').pop()?.toLowerCase();
  return ext === 'pdf';
};

const formatDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatDateTime = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const formatSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
};

// Derive a consistent status from the doc model
const getDocStatus = (doc) => {
  if (doc.status) return doc.status; // already transformed
  if (doc.verified === true) return 'verified';
  if (doc.verified === false && doc.verified_at) return 'rejected';
  return 'pending';
};

const STATUS_CONF = {
  pending: { label: 'Pending', color: '#d97706', bg: '#fef3c7', border: '#fbbf24', icon: <Schedule sx={{ fontSize: 14 }} /> },
  verified: { label: 'Verified', color: '#16a34a', bg: '#dcfce7', border: '#22c55e', icon: <CheckCircle sx={{ fontSize: 14 }} /> },
  rejected: { label: 'Rejected', color: '#dc2626', bg: '#fecaca', border: '#f87171', icon: <Cancel sx={{ fontSize: 14 }} /> },
  suspicious: { label: 'Suspicious', color: '#9333ea', bg: '#f3e8ff', border: '#a855f7', icon: <WarningAmber sx={{ fontSize: 14 }} /> },
};

// ═══════════════════════════════════════════════════════════════
// KPI CARD
// ═══════════════════════════════════════════════════════════════

const KPICard = ({ icon, label, value, color, onClick, active }) => (
  <Card
    onClick={onClick}
    sx={{
      borderRadius: 2, cursor: onClick ? 'pointer' : 'default',
      border: active ? `2px solid ${color}` : '1px solid #e2e8f0',
      transition: 'all 0.2s ease',
      background: active ? `${color}08` : '#fff',
      '&:hover': onClick ? { transform: 'translateY(-2px)', boxShadow: `0 4px 16px ${color}20`, borderColor: color } : {},
    }}
  >
    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
      <Box className="flex items-center justify-between">
        <Box>
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
            {label}
          </Typography>
          <Typography sx={{ fontSize: '1.4rem', fontWeight: 800, color, lineHeight: 1.2, mt: 0.3 }}>
            {value}
          </Typography>
        </Box>
        <Box sx={{ width: 36, height: 36, borderRadius: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: `${color}10` }}>
          {React.cloneElement(icon, { sx: { fontSize: 20, color } })}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

// ═══════════════════════════════════════════════════════════════
// AUDIT TIMELINE
// ═══════════════════════════════════════════════════════════════

const AuditTimeline = ({ doc }) => {
  if (!doc) return null;

  const events = [];
  events.push({ time: doc.created_at, label: 'Document Uploaded', color: '#2563eb', icon: <Description sx={{ fontSize: 14 }} /> });
  if (doc.verified_at) {
    const status = getDocStatus(doc);
    if (status === 'verified') {
      events.push({ time: doc.verified_at, label: `Verified${doc.verified_by ? ` by #${doc.verified_by}` : ''}`, color: '#16a34a', icon: <CheckCircle sx={{ fontSize: 14 }} /> });
    } else if (status === 'rejected') {
      events.push({ time: doc.verified_at, label: `Rejected${doc.verified_by ? ` by #${doc.verified_by}` : ''}`, color: '#dc2626', icon: <Cancel sx={{ fontSize: 14 }} /> });
    }
  }
  if (doc.comments) {
    events.push({ time: doc.verified_at || doc.updated_at || doc.created_at, label: `Remark: "${doc.comments}"`, color: '#64748b', icon: <StickyNote2 sx={{ fontSize: 14 }} /> });
  }

  // Sort by time
  events.sort((a, b) => new Date(a.time) - new Date(b.time));

  return (
    <Box>
      <Box className="flex items-center gap-1.5 mb-1.5">
        <Timeline sx={{ fontSize: 14, color: '#2563eb' }} />
        <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#2563eb', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
          Audit Trail
        </Typography>
      </Box>
      <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#e2e8f0' }}>
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          {events.map((evt, i) => (
            <Box key={i} className="flex items-start gap-2" sx={{ mb: i < events.length - 1 ? 1.5 : 0 }}>
              {/* Timeline dot & line */}
              <Box className="flex flex-col items-center" sx={{ minWidth: 16 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: evt.color, flexShrink: 0, mt: 0.3 }} />
                {i < events.length - 1 && <Box sx={{ width: 1, flex: 1, bgcolor: '#e2e8f0', mt: 0.5, minHeight: 16 }} />}
              </Box>
              <Box className="min-w-0 flex-1">
                <Box className="flex items-center gap-1">
                  {evt.icon}
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#1e293b' }}>{evt.label}</Typography>
                </Box>
                <Typography sx={{ fontSize: '0.62rem', color: '#94a3b8' }}>{formatDateTime(evt.time)}</Typography>
              </Box>
            </Box>
          ))}
        </CardContent>
      </Card>
    </Box>
  );
};

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════

const DocumentVerification = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [selectedDoc, setSelectedDoc] = useState(null);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [tagFilter, setTagFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  // Action dialog
  const [actionDialog, setActionDialog] = useState(false);
  const [actionType, setActionType] = useState('');
  const [actionNotes, setActionNotes] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // ── Load Data ──
  const loadData = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) setRefreshing(true); else setLoading(true);

      // getAllForUser fetches all policy docs
      const docs = await documentsAPI.getAllForUser();
      const normalised = (Array.isArray(docs) ? docs : []).map(d => ({
        ...d,
        status: getDocStatus(d),
        document_type: d.doc_type || d.document_type || 'other',
      }));
      setDocuments(normalised);
    } catch (err) {
      console.error('Error loading documents:', err);
      setSnackbar({ open: true, message: 'Failed to load documents', severity: 'error' });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Stats ──
  const stats = useMemo(() => {
    const total = documents.length;
    const pending = documents.filter(d => d.status === 'pending').length;
    const verified = documents.filter(d => d.status === 'verified').length;
    const rejected = documents.filter(d => d.status === 'rejected').length;
    const highRisk = documents.filter(d => d.status === 'pending' && isHighRisk(d)).length;
    return { total, pending, verified, rejected, highRisk };
  }, [documents]);

  // ── Filtering ──
  const filteredDocs = useMemo(() => {
    return documents.filter(d => {
      if (searchTerm) {
        const q = searchTerm.toLowerCase();
        const match = (d.file_name || '').toLowerCase().includes(q)
          || (d.title || '').toLowerCase().includes(q)
          || (d.description || '').toLowerCase().includes(q)
          || (d.user_name || '').toLowerCase().includes(q)
          || String(d.id).includes(q);
        if (!match) return false;
      }
      if (statusFilter !== 'all' && d.status !== statusFilter) return false;
      if (tagFilter !== 'all') {
        const tag = getDocTag(d.document_type);
        if (tag.label !== tagFilter) return false;
      }
      return true;
    });
  }, [documents, searchTerm, statusFilter, tagFilter]);

  // ── Actions ──
  const handleVerify = () => {
    setActionType('verified');
    setActionNotes('');
    setActionDialog(true);
  };
  const handleReject = () => {
    setActionType('rejected');
    setActionNotes('');
    setActionDialog(true);
  };
  const handleSuspicious = () => {
    setActionType('suspicious');
    setActionNotes('');
    setActionDialog(true);
  };

  const handleActionConfirm = async () => {
    try {
      if (!selectedDoc) return;
      if (actionType === 'suspicious') {
        // Mark as suspicious = reject with a flag note
        await documentsAPI.verify(selectedDoc.id, false, `[SUSPICIOUS] ${actionNotes}`);
        setSnackbar({ open: true, message: 'Document marked as suspicious', severity: 'warning' });
      } else {
        const verified = actionType === 'verified';
        await documentsAPI.verify(selectedDoc.id, verified, actionNotes);
        setSnackbar({ open: true, message: `Document ${verified ? 'verified' : 'rejected'} successfully`, severity: 'success' });
      }
      setActionDialog(false);
      setSelectedDoc(null);
      loadData(true);
    } catch (err) {
      console.error('Error verifying document:', err);
      setSnackbar({ open: true, message: 'Failed to process document', severity: 'error' });
    }
  };

  // Build a file URL for preview
  const getPreviewUrl = (doc) => {
    if (!doc) return null;
    if (doc.file_path) {
      // If file_path looks like a URL, use it directly; otherwise construct one
      if (doc.file_path.startsWith('http')) return doc.file_path;
      // Try constructing from API base
      return `/uploads/${doc.file_path.split('/').pop()}`;
    }
    return null;
  };

  // ── Loading ──
  if (loading && documents.length === 0) {
    return (
      <Box className="flex flex-col items-center justify-center" sx={{ height: '60vh' }}>
        <Box sx={{ width: 48, height: 48, border: '4px solid #e2e8f0', borderTop: '4px solid #2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite', '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } } }} />
        <Typography sx={{ color: '#64748b', mt: 2, fontWeight: 500 }}>Loading documents…</Typography>
      </Box>
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f1f5f9', p: { xs: 1.5, md: 2.5 } }}>
      {refreshing && <LinearProgress sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999 }} />}

      {/* ══════════ 1. KPI STRIP ══════════ */}
      <Box className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5 mb-3">
        <KPICard icon={<Assignment />} label="Total Uploaded" value={stats.total} color="#2563eb"
          onClick={() => { setStatusFilter('all'); setTagFilter('all'); }} active={statusFilter === 'all' && tagFilter === 'all'} />
        <KPICard icon={<PendingActions />} label="Pending" value={stats.pending} color="#d97706"
          onClick={() => { setStatusFilter('pending'); setTagFilter('all'); }} active={statusFilter === 'pending'} />
        <KPICard icon={<CheckCircle />} label="Verified" value={stats.verified} color="#16a34a"
          onClick={() => { setStatusFilter('verified'); setTagFilter('all'); }} active={statusFilter === 'verified'} />
        <KPICard icon={<Cancel />} label="Rejected" value={stats.rejected} color="#dc2626"
          onClick={() => { setStatusFilter('rejected'); setTagFilter('all'); }} active={statusFilter === 'rejected'} />
        <KPICard icon={<WarningAmber />} label="High Risk" value={stats.highRisk} color="#9333ea" />
      </Box>

      {/* ══════════ 2. FILTER BAR ══════════ */}
      <Card sx={{ borderRadius: 2, mb: 2, border: '1px solid #e2e8f0' }}>
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Box className="flex flex-wrap items-center gap-2">
            <TextField
              size="small" placeholder="Search file, description, user…"
              value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ minWidth: 240, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#f8fafc' } }}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment> }}
            />
            <Button size="small" variant={showFilters ? 'contained' : 'outlined'}
              onClick={() => setShowFilters(!showFilters)} startIcon={<FilterList />}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
              Filters {showFilters ? <ExpandLess sx={{ ml: 0.5 }} /> : <ExpandMore sx={{ ml: 0.5 }} />}
            </Button>

            {/* 3. Tag filter chips */}
            <Box className="flex items-center gap-1 ml-1">
              {Object.values(TAG_CATEGORIES).map(cat => (
                <Chip key={cat.label} label={cat.label} size="small"
                  onClick={() => setTagFilter(tagFilter === cat.label ? 'all' : cat.label)}
                  sx={{
                    height: 24, fontSize: '0.65rem', fontWeight: 700, cursor: 'pointer',
                    bgcolor: tagFilter === cat.label ? cat.bg : '#f1f5f9',
                    color: tagFilter === cat.label ? cat.color : '#94a3b8',
                    border: tagFilter === cat.label ? `1px solid ${cat.color}` : '1px solid transparent',
                    '&:hover': { bgcolor: cat.bg, color: cat.color },
                  }}
                />
              ))}
            </Box>

            <Box className="flex-1" />
            <Tooltip title="Refresh"><IconButton size="small" onClick={() => loadData(true)} disabled={refreshing}><Refresh sx={{ fontSize: 20 }} /></IconButton></Tooltip>
            <Chip label={`${filteredDocs.length} of ${documents.length}`} size="small" sx={{ fontWeight: 600, bgcolor: '#f1f5f9', color: '#64748b' }} />
          </Box>

          <Collapse in={showFilters}>
            <Box className="flex flex-wrap items-center gap-2 mt-2 pt-2" sx={{ borderTop: '1px solid #e2e8f0' }}>
              <FormControl size="small" sx={{ minWidth: 130 }}>
                <InputLabel>Status</InputLabel>
                <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)} sx={{ borderRadius: 2, bgcolor: '#f8fafc' }}>
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="verified">Verified</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Category</InputLabel>
                <Select value={tagFilter} label="Category" onChange={(e) => setTagFilter(e.target.value)} sx={{ borderRadius: 2, bgcolor: '#f8fafc' }}>
                  <MenuItem value="all">All Categories</MenuItem>
                  {Object.values(TAG_CATEGORIES).map(cat => (
                    <MenuItem key={cat.label} value={cat.label}>{cat.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {(statusFilter !== 'all' || tagFilter !== 'all' || searchTerm) && (
                <Button size="small" onClick={() => { setStatusFilter('all'); setTagFilter('all'); setSearchTerm(''); }}
                  sx={{ textTransform: 'none', color: '#dc2626', fontWeight: 600 }}>Clear All</Button>
              )}
            </Box>
          </Collapse>
        </CardContent>
      </Card>

      {/* ══════════ 3. THREE-COLUMN LAYOUT ══════════ */}
      <Box className="flex gap-2.5" sx={{ height: 'calc(100vh - 300px)', minHeight: 500 }}>

        {/* ── LEFT: Document Queue ── */}
        <Card sx={{ flex: '0 0 280px', borderRadius: 2, border: '1px solid #e2e8f0', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ px: 1.5, py: 1, bgcolor: '#0f172a', flexShrink: 0 }}>
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Document Queue ({filteredDocs.length})
            </Typography>
          </Box>
          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            {filteredDocs.length === 0 ? (
              <Box className="flex flex-col items-center justify-center h-full" sx={{ px: 2 }}>
                <VerifiedUser sx={{ fontSize: 40, color: '#cbd5e1', mb: 1 }} />
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#94a3b8', textAlign: 'center' }}>
                  {statusFilter === 'pending' ? 'Review queue is clear!' : 'No documents match current filters'}
                </Typography>
                <Typography sx={{ fontSize: '0.68rem', color: '#cbd5e1', textAlign: 'center', mt: 0.5 }}>
                  {statusFilter === 'pending' ? 'All documents have been processed. Great job!' : 'Try adjusting the filters above.'}
                </Typography>
              </Box>
            ) : (
              filteredDocs.map((doc) => {
                const status = doc.status;
                const sConf = STATUS_CONF[status] || STATUS_CONF.pending;
                const tag = getDocTag(doc.document_type);
                const isActive = selectedDoc?.id === doc.id;
                const highRisk = isHighRisk(doc);

                return (
                  <Box
                    key={doc.id}
                    onClick={() => setSelectedDoc(doc)}
                    sx={{
                      px: 1.5, py: 1.2, cursor: 'pointer',
                      borderBottom: '1px solid #f1f5f9',
                      borderLeft: `3px solid ${isActive ? '#2563eb' : sConf.border}`,
                      bgcolor: isActive ? '#eff6ff' : 'transparent',
                      transition: 'all 0.15s ease',
                      '&:hover': { bgcolor: isActive ? '#eff6ff' : '#f8fafc' },
                    }}
                  >
                    <Box className="flex items-start gap-1.5">
                      <Avatar sx={{ width: 28, height: 28, bgcolor: tag.bg, color: tag.color, fontSize: '0.55rem', fontWeight: 800, mt: 0.2 }}>
                        {getDocIcon(doc.file_name)}
                      </Avatar>
                      <Box className="min-w-0 flex-1">
                        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#1e293b', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {doc.title || doc.file_name || `Doc #${doc.id}`}
                        </Typography>
                        <Box className="flex items-center gap-1 mt-0.5 flex-wrap">
                          <Chip label={tag.label} size="small" sx={{ height: 16, fontSize: '0.55rem', fontWeight: 700, bgcolor: tag.bg, color: tag.color }} />
                          <Chip label={sConf.label} size="small" icon={sConf.icon}
                            sx={{ height: 16, fontSize: '0.55rem', fontWeight: 700, bgcolor: sConf.bg, color: sConf.color, '& .MuiChip-icon': { color: sConf.color, fontSize: 12 } }} />
                          {highRisk && status === 'pending' && (
                            <WarningAmber sx={{ fontSize: 12, color: '#dc2626' }} />
                          )}
                        </Box>
                        <Typography sx={{ fontSize: '0.6rem', color: '#94a3b8', mt: 0.3 }}>
                          {formatDate(doc.created_at)} • {formatSize(doc.file_size)}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                );
              })
            )}
          </Box>
        </Card>

        {/* ── CENTER: Preview Panel ── */}
        <Card sx={{ flex: 1, borderRadius: 2, border: '1px solid #e2e8f0', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {!selectedDoc ? (
            <Box className="flex flex-col items-center justify-center h-full" sx={{ color: '#cbd5e1' }}>
              <Visibility sx={{ fontSize: 64, opacity: 0.2, mb: 2 }} />
              <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#94a3b8' }}>Select a document to preview</Typography>
              <Typography sx={{ fontSize: '0.75rem', color: '#cbd5e1', mt: 0.5 }}>Click on any item in the queue</Typography>
            </Box>
          ) : (
            <>
              {/* Preview Header */}
              <Box sx={{ px: 2, py: 1, bgcolor: '#0f172a', flexShrink: 0 }}>
                <Box className="flex items-center justify-between">
                  <Box className="flex items-center gap-2 min-w-0">
                    {getDocIcon(selectedDoc.file_name)}
                    <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.8rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {selectedDoc.title || selectedDoc.file_name || `Document #${selectedDoc.id}`}
                    </Typography>
                    {(() => {
                      const tag = getDocTag(selectedDoc.document_type);
                      return <Chip label={tag.label} size="small" sx={{ height: 18, fontSize: '0.58rem', fontWeight: 700, bgcolor: tag.bg, color: tag.color }} />;
                    })()}
                  </Box>
                  <IconButton size="small" onClick={() => setSelectedDoc(null)} sx={{ color: '#94a3b8' }}>
                    <Close sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              </Box>

              {/* Preview Area */}
              <Box sx={{ flex: 1, overflow: 'auto', bgcolor: '#1e293b', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                {(() => {
                  const url = getPreviewUrl(selectedDoc);
                  if (!url) {
                    return (
                      <Box className="text-center">
                        <InsertDriveFile sx={{ fontSize: 64, color: '#475569', mb: 1 }} />
                        <Typography sx={{ color: '#94a3b8', fontSize: '0.8rem' }}>Preview not available</Typography>
                        <Typography sx={{ color: '#64748b', fontSize: '0.7rem', mt: 0.5 }}>{selectedDoc.file_name}</Typography>
                      </Box>
                    );
                  }
                  if (isImageFile(selectedDoc.file_name)) {
                    return (
                      <Box
                        component="img"
                        src={url}
                        alt={selectedDoc.file_name}
                        sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', p: 2 }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    );
                  }
                  if (isPdfFile(selectedDoc.file_name)) {
                    return (
                      <Box component="iframe" src={url} sx={{ width: '100%', height: '100%', border: 'none' }} title="PDF Preview" />
                    );
                  }
                  return (
                    <Box className="text-center">
                      <InsertDriveFile sx={{ fontSize: 64, color: '#475569', mb: 1 }} />
                      <Typography sx={{ color: '#94a3b8', fontSize: '0.8rem' }}>
                        {selectedDoc.file_name}
                      </Typography>
                      <Button size="small" variant="outlined" href={url} target="_blank" rel="noopener"
                        sx={{ mt: 1.5, color: '#94a3b8', borderColor: '#475569', textTransform: 'none', fontSize: '0.72rem' }}>
                        Download to View
                      </Button>
                    </Box>
                  );
                })()}
              </Box>

              {/* 4. Audit Timeline below preview */}
              <Box sx={{ px: 2, py: 1.5, borderTop: '1px solid #e2e8f0', bgcolor: '#f8fafc', flexShrink: 0 }}>
                <AuditTimeline doc={selectedDoc} />
              </Box>
            </>
          )}
        </Card>

        {/* ── RIGHT: Verification Control Panel ── */}
        <Card sx={{ flex: '0 0 280px', borderRadius: 2, border: '1px solid #e2e8f0', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ px: 1.5, py: 1, bgcolor: '#0f172a', flexShrink: 0 }}>
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Verification Panel
            </Typography>
          </Box>

          {!selectedDoc ? (
            <Box className="flex flex-col items-center justify-center h-full" sx={{ color: '#cbd5e1', px: 2 }}>
              <Shield sx={{ fontSize: 40, opacity: 0.2, mb: 1 }} />
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#94a3b8', textAlign: 'center' }}>
                Select a document to verify
              </Typography>
            </Box>
          ) : (
            <Box sx={{ flex: 1, overflowY: 'auto', p: 1.5, display: 'flex', flexDirection: 'column' }}>
              {/* Document Info */}
              <Box sx={{ mb: 2 }}>
                <Box className="flex items-center gap-1 mb-1">
                  <Description sx={{ fontSize: 14, color: '#2563eb' }} />
                  <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#2563eb', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                    Document Info
                  </Typography>
                </Box>
                <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#e2e8f0' }}>
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <InfoRow label="ID" value={`#${selectedDoc.id}`} />
                    <Divider sx={{ my: 0.5 }} />
                    <InfoRow label="Type" value={DOC_TYPE_LABELS[selectedDoc.document_type] || selectedDoc.document_type} />
                    <Divider sx={{ my: 0.5 }} />
                    <InfoRow label="Status" value={
                      <Chip label={(STATUS_CONF[selectedDoc.status] || STATUS_CONF.pending).label} size="small"
                        sx={{ height: 18, fontSize: '0.58rem', fontWeight: 700,
                          bgcolor: (STATUS_CONF[selectedDoc.status] || STATUS_CONF.pending).bg,
                          color: (STATUS_CONF[selectedDoc.status] || STATUS_CONF.pending).color }} />
                    } />
                    <Divider sx={{ my: 0.5 }} />
                    <InfoRow label="File" value={selectedDoc.file_name} />
                    <Divider sx={{ my: 0.5 }} />
                    <InfoRow label="Size" value={formatSize(selectedDoc.file_size)} />
                    <Divider sx={{ my: 0.5 }} />
                    <InfoRow label="Uploaded" value={formatDate(selectedDoc.created_at)} />
                    {selectedDoc.policy_id && (
                      <>
                        <Divider sx={{ my: 0.5 }} />
                        <InfoRow label="Policy" value={`#${selectedDoc.policy_id}`} />
                      </>
                    )}
                  </CardContent>
                </Card>
              </Box>

              {/* Tags */}
              <Box sx={{ mb: 2 }}>
                <Box className="flex items-center gap-1 mb-1">
                  <LocalOffer sx={{ fontSize: 14, color: '#d97706' }} />
                  <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#d97706', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                    Tags
                  </Typography>
                </Box>
                <Box className="flex flex-wrap gap-1">
                  {(() => {
                    const tag = getDocTag(selectedDoc.document_type);
                    return <Chip label={tag.label} size="small" sx={{ height: 22, fontSize: '0.62rem', fontWeight: 700, bgcolor: tag.bg, color: tag.color }} />;
                  })()}
                  {isHighRisk(selectedDoc) && (
                    <Chip label="High Risk" size="small" icon={<WarningAmber sx={{ fontSize: 14 }} />}
                      sx={{ height: 22, fontSize: '0.62rem', fontWeight: 700, bgcolor: '#fef2f2', color: '#dc2626', '& .MuiChip-icon': { color: '#dc2626' } }} />
                  )}
                  {selectedDoc.mime_type && (
                    <Chip label={selectedDoc.mime_type.split('/').pop().toUpperCase()} size="small"
                      sx={{ height: 22, fontSize: '0.62rem', fontWeight: 600, bgcolor: '#f1f5f9', color: '#64748b' }} />
                  )}
                </Box>
              </Box>

              {/* Existing comments */}
              {selectedDoc.comments && (
                <Box sx={{ mb: 2 }}>
                  <Box className="flex items-center gap-1 mb-1">
                    <StickyNote2 sx={{ fontSize: 14, color: '#64748b' }} />
                    <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                      Remarks
                    </Typography>
                  </Box>
                  <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#e2e8f0' }}>
                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Typography sx={{ fontSize: '0.75rem', color: '#475569', lineHeight: 1.5, fontStyle: 'italic' }}>
                        "{selectedDoc.comments}"
                      </Typography>
                    </CardContent>
                  </Card>
                </Box>
              )}

              {/* Spacer */}
              <Box sx={{ flex: 1 }} />

              {/* Action Buttons */}
              {selectedDoc.status === 'pending' ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pt: 1.5, borderTop: '1px solid #e2e8f0' }}>
                  <Button fullWidth variant="contained" size="small" startIcon={<CheckCircle />}
                    onClick={handleVerify}
                    sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2, py: 0.8, bgcolor: '#16a34a', '&:hover': { bgcolor: '#15803d' } }}>
                    Approve
                  </Button>
                  <Button fullWidth variant="contained" size="small" startIcon={<Cancel />}
                    onClick={handleReject}
                    sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2, py: 0.8, bgcolor: '#dc2626', '&:hover': { bgcolor: '#b91c1c' } }}>
                    Reject
                  </Button>
                  <Button fullWidth variant="outlined" size="small" startIcon={<Flag />}
                    onClick={handleSuspicious}
                    sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2, py: 0.8, borderColor: '#9333ea', color: '#9333ea', '&:hover': { bgcolor: '#f3e8ff', borderColor: '#7c3aed' } }}>
                    Mark Suspicious
                  </Button>
                </Box>
              ) : (
                <Box sx={{ pt: 1.5, borderTop: '1px solid #e2e8f0' }}>
                  <Alert severity={selectedDoc.status === 'verified' ? 'success' : 'error'} sx={{ borderRadius: 2, '& .MuiAlert-message': { fontSize: '0.72rem' } }}>
                    This document has been {selectedDoc.status === 'verified' ? 'verified' : 'rejected'}.
                  </Alert>
                </Box>
              )}
            </Box>
          )}
        </Card>
      </Box>

      {/* ══════════ ACTION DIALOG ══════════ */}
      <Dialog open={actionDialog} onClose={() => setActionDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{
          bgcolor: actionType === 'verified' ? '#f0fdf4' : actionType === 'suspicious' ? '#f3e8ff' : '#fef2f2',
          fontWeight: 700, fontSize: '1rem',
        }}>
          {actionType === 'verified' ? '✓ Approve Document' : actionType === 'suspicious' ? '⚠ Mark as Suspicious' : '✕ Reject Document'}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {selectedDoc && (
            <Box>
              <Box className="grid grid-cols-2 gap-2 mb-3">
                <Box>
                  <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8', textTransform: 'uppercase' }}>Document</Typography>
                  <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#1e293b' }}>#{selectedDoc.id}</Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8', textTransform: 'uppercase' }}>Type</Typography>
                  <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#1e293b' }}>
                    {DOC_TYPE_LABELS[selectedDoc.document_type] || selectedDoc.document_type}
                  </Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8', textTransform: 'uppercase' }}>File</Typography>
                  <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {selectedDoc.file_name}
                  </Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8', textTransform: 'uppercase' }}>Uploaded</Typography>
                  <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#1e293b' }}>{formatDate(selectedDoc.created_at)}</Typography>
                </Box>
              </Box>
              <TextField
                autoFocus fullWidth multiline rows={3} size="small"
                label={actionType === 'verified' ? 'Verification Notes (optional)' : actionType === 'suspicious' ? 'Suspicion Reason *' : 'Rejection Reason *'}
                value={actionNotes} onChange={(e) => setActionNotes(e.target.value)}
                placeholder={actionType === 'verified' ? 'Optional verification notes…' : 'Please provide a reason…'}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
              {actionType === 'verified' && (
                <Alert severity="success" sx={{ mt: 1.5, borderRadius: 2 }}>Document will be marked as verified and the user will be notified.</Alert>
              )}
              {actionType === 'suspicious' && (
                <Alert severity="warning" sx={{ mt: 1.5, borderRadius: 2 }}>Document will be flagged for further investigation.</Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setActionDialog(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button onClick={handleActionConfirm} variant="contained"
            disabled={(actionType === 'rejected' || actionType === 'suspicious') && !actionNotes.trim()}
            sx={{
              textTransform: 'none', fontWeight: 700, borderRadius: 2,
              bgcolor: actionType === 'verified' ? '#16a34a' : actionType === 'suspicious' ? '#9333ea' : '#dc2626',
              '&:hover': { bgcolor: actionType === 'verified' ? '#15803d' : actionType === 'suspicious' ? '#7c3aed' : '#b91c1c' },
            }}>
            {actionType === 'verified' ? 'Confirm Approve' : actionType === 'suspicious' ? 'Confirm Flag' : 'Confirm Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%', borderRadius: 2 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// ── Small helper component ──
const InfoRow = ({ label, value }) => (
  <Box className="flex items-center justify-between py-0.5">
    <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8', minWidth: 60 }}>{label}</Typography>
    {typeof value === 'string' || typeof value === 'number' ? (
      <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#1e293b', textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 160 }}>
        {value || '—'}
      </Typography>
    ) : value}
  </Box>
);

export default DocumentVerification;
